import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { ArrowForward, Check } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { projectPath } from '../../utils/navigation';

/** Pipeline steps, in order. */
export const PIPELINE_STEPS = [
  { key: 'sessions', eyebrow: 'Capture', title: 'Sessions' },
  { key: 'requirements', eyebrow: 'Distill', title: 'Requirements' },
  { key: 'documents', eyebrow: 'Generate', title: 'Documents' },
];

/**
 * Plural helper.
 * @param {number} n - Count.
 * @param {string} one - Singular.
 * @param {string} [many] - Plural.
 * @returns {string} "1 thing" / "2 things".
 */
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/**
 * Default summary line per step.
 * @param {string} key - Step key.
 * @param {{sessions: number, requirements: number, documents: number}} counts - Counts.
 * @returns {string} Summary.
 */
function defaultSummary(key, counts) {
  if (key === 'sessions') return counts.sessions ? plural(counts.sessions, 'meeting') : 'No recordings yet';
  if (key === 'requirements') return counts.requirements ? `${counts.requirements} extracted` : 'None yet';
  return counts.documents ? `${counts.documents} generated` : 'None yet';
}

/**
 * The next thing to do in the pipeline, or null when every step has output.
 * @param {{sessions: number, requirements: number, documents: number}} counts - Counts.
 * @returns {{step: string, label: string, description: string}|null} Next action.
 */
export function nextPipelineAction(counts) {
  if (!counts.sessions) return { step: 'sessions', label: 'Add a recording', description: 'Record or upload a meeting to get started.' };
  if (!counts.requirements) return { step: 'requirements', label: 'Extract requirements', description: `Pull requirements out of ${plural(counts.sessions, 'meeting')}.` };
  if (!counts.documents) return { step: 'documents', label: 'Generate document', description: `Turn ${plural(counts.requirements, 'requirement')} into a document from a template.` };
  return null;
}

/**
 * Project pipeline strip: Sessions -> Requirements -> Documents, with counts, the current step
 * highlighted and an optional "next step" call to action.
 *
 * Props:
 * - project (object from AppContext) or projectId (resolved from AppContext).
 * - active: 'sessions'|'requirements'|'documents' - the step the page is showing.
 * - counts: optional {sessions, requirements, documents} overriding the project's arrays.
 * - summaries: optional {sessions?, requirements?, documents?} summary lines.
 * - onStepSelect(step): called when a step is clicked (default: navigate to `/projects/:id?tab=<step>`).
 * - onNextAction(step): when given, shows the next-step button (e.g. 'requirements' -> start extraction).
 * - nextActionLabels: optional {sessions?, requirements?, documents?} button labels.
 * @param {object} props - Props.
 * @returns {JSX.Element|null} Stepper.
 */
export default function PipelineStepper({ project: projectProp, projectId, active, counts: countsProp, summaries = {}, onStepSelect, onNextAction, nextActionLabels = {}, sx }) {
  const navigate = useNavigate();
  const { state } = useApp();
  const project = projectProp || (projectId ? state.projects.find((p) => p.id === projectId) : null);
  const id = project?.id || projectId;
  if (!id) return null;

  const counts = {
    sessions: countsProp?.sessions ?? project?.sessions?.length ?? 0,
    requirements: countsProp?.requirements ?? project?.requirements?.length ?? 0,
    documents: countsProp?.documents ?? project?.generatedDocuments?.length ?? 0,
  };
  const next = nextPipelineAction(counts);

  const select = (step) => {
    if (onStepSelect) onStepSelect(step);
    else navigate(projectPath(id, step));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ...sx }}>
      <Box
        component="nav"
        aria-label="Project pipeline"
        sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 1 }}
      >
        {PIPELINE_STEPS.map((step, index) => {
          const isActive = step.key === active;
          const done = counts[step.key] > 0;
          return (
            <Box
              key={step.key}
              component="button"
              type="button"
              onClick={() => select(step.key)}
              aria-current={isActive ? 'step' : undefined}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.5,
                textAlign: 'left',
                font: 'inherit',
                color: 'var(--q-text, #1C1B19)',
                cursor: 'pointer',
                borderRadius: '14px',
                border: isActive ? '1.5px solid var(--q-accent, #4F3BD6)' : '1px solid var(--q-border, #E3E0DA)',
                bgcolor: isActive ? 'var(--q-accent-tint, #F7F5FE)' : 'var(--q-surface, #FFFFFF)',
                boxShadow: isActive ? '0 0 0 3px var(--q-focus-halo, rgba(79,59,214,0.10))' : 'none',
                transition: 'border-color 120ms ease, background-color 120ms ease',
                '&:hover': { borderColor: isActive ? 'var(--q-accent, #4F3BD6)' : 'var(--q-border-hover, #C9C4BB)' },
                '&:focus-visible': { outline: '2px solid var(--q-focus-ring, #4F3BD6)', outlineOffset: 2 },
              }}
            >
              <Box
                component="span"
                aria-hidden="true"
                sx={{
                  width: 32,
                  height: 32,
                  flex: 'none',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 13,
                  fontWeight: 700,
                  ...(isActive
                    ? { bgcolor: 'var(--q-accent, #4F3BD6)', color: 'var(--q-on-accent, #FFFFFF)' }
                    : done
                      ? { bgcolor: 'var(--q-success-soft, #E3F2E8)', color: 'var(--q-success-text, #17603A)' }
                      : { bgcolor: 'var(--q-subtle, #F1EFEB)', color: 'var(--q-text-3, #4A463F)' }),
                }}
              >
                {done && !isActive ? <Check sx={{ fontSize: 17 }} /> : index + 1}
              </Box>
              <Box component="span" sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <Typography component="span" sx={{ fontSize: 12, color: isActive ? 'var(--q-accent-text, #3A28B8)' : 'var(--q-muted-2, #6B665F)' }}>
                  {index + 1} · {step.eyebrow}
                </Typography>
                <Typography component="span" sx={{ fontWeight: 600, fontSize: 14 }}>
                  {step.title}
                </Typography>
                <Typography component="span" noWrap sx={{ fontSize: 12.5, color: 'var(--q-muted, #5E5A54)' }}>
                  {summaries[step.key] || defaultSummary(step.key, counts)}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
      {onNextAction && next && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1.25,
            borderRadius: 'var(--q-radius-xl, 12px)',
            bgcolor: 'var(--q-subtle, #F1EFEB)',
          }}
        >
          <Typography variant="body2" sx={{ flex: '1 1 220px', color: 'var(--q-text-2, #3D3A35)' }}>
            <Box component="span" sx={{ fontWeight: 600 }}>Next:</Box> {next.description}
          </Typography>
          <Button
            size="small"
            variant="contained"
            endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
            onClick={() => onNextAction(next.step)}
            sx={{ borderRadius: 'var(--q-radius-pill, 999px)', textTransform: 'none', fontWeight: 600 }}
          >
            {nextActionLabels[next.step] || next.label}
          </Button>
        </Box>
      )}
    </Box>
  );
}
